import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Create a Memorial', href: '/create-memorial' },
    { name: 'Memorial Styles', href: '/memorial-styles' },
    { name: 'Articles', href: '/articles' },
    { name: 'Contact', href: '/get-in-touch' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="absolute w-full top-0 z-50">
      <div className="pt-8 md:pt-6 lg:pt-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <nav className="flex h-24 items-center justify-between">
            {/* Logo/Brand */}
            <Link 
              to="/" 
              className="flex items-center -ml-3 relative z-50"
            >
              <img 
                src="/images/Logo.svg" 
                alt="Digital Memorials"
                className="h-40 w-auto transform hover:scale-105 transition-transform duration-300"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:space-x-16">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    font-montserrat text-base lg:text-lg tracking-wider transition-colors duration-200
                    ${isActive(item.href) 
                      ? 'text-primary font-medium' 
                      : 'text-[#FFFFFFCC] hover:text-white'}
                  `}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              className="lg:hidden relative z-50 w-12 h-12 flex items-center justify-center"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-label="Toggle navigation menu"
            >
              <div className="relative w-8 h-6">
                <span className={`absolute left-0 top-0 block w-8 h-0.5 transform transition duration-500 ease-in-out ${isMenuOpen ? 'rotate-45 translate-y-[11px] bg-primary' : 'bg-white'}`} />
                <span className={`absolute left-0 top-[11px] block w-8 h-0.5 transform transition duration-500 ease-in-out ${isMenuOpen ? 'opacity-0 bg-primary' : 'bg-white'}`} />
                <span className={`absolute left-0 bottom-0 block w-8 h-0.5 transform transition duration-500 ease-in-out ${isMenuOpen ? '-rotate-45 -translate-y-[11px] bg-primary' : 'bg-white'}`} />
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`lg:hidden fixed inset-0 bg-[#0A0A0A]/95 backdrop-blur-lg transform transition-transform duration-500 ease-in-out ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="min-h-screen flex flex-col justify-center px-6">
          {/* Navigation Links */}
          <div className="space-y-8">
            {navigation.map((item, index) => (
              <div
                key={item.name}
                className={`transform transition-all duration-500 delay-${index * 100} ${
                  isMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
                }`}
              >
                <Link
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`
                    block text-3xl font-montserrat font-semibold tracking-wide
                    ${isActive(item.href)
                      ? 'text-primary'
                      : 'text-white hover:text-primary transition-colors duration-300'}
                  `}
                >
                  <div className="flex items-center group">
                    <span className="relative">
                      {item.name}
                      <span className={`absolute -bottom-2 left-0 w-0 h-0.5 bg-primary transition-all duration-300 ${
                        isActive(item.href) ? 'w-full' : 'group-hover:w-full'
                      }`} />
                    </span>
                    <svg
                      className={`w-6 h-6 ml-3 transform transition-transform duration-300 ${
                        isActive(item.href) ? 'translate-x-2 text-primary' : 'text-white group-hover:translate-x-2 group-hover:text-primary'
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* Decorative Elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 